import axios, { AxiosRequestConfig } from 'axios';
import { stringify } from 'qs';

import { captureSentryException } from './captureSentryError';

const apiHost = process.env.REACT_APP_API_HOST;

/** paramsSerializer と同じ。クエリ文字列はインターセプタの長さ計算と一致させる */
const QS_PARAMS_OPTIONS = { arrayFormat: 'brackets' as const, encode: false };

const utf8ByteLengthEncoder = new TextEncoder();

/*
 * --- ALB「Request line」に近い長さの Sentry 検知（レビュー用の説明）---
 * [目的]
 *   params 付きでクエリが巨大化したとき、ALB 側で 414 や 431 等になり得る事象を早めに把握する。
 *   リクエストは止めず、そのまま送信する（検知のみ）。
 * [閾値の根拠]
 *   AWS ドキュメント「HTTP headers」の Request line 16K。HTTP/2 との対応は同ページ未記載のため近似。
 *   https://docs.aws.amazon.com/elasticloadbalancing/latest/application/load-balancer-limits.html#http-headers-quotas
 * [数えるもの]
 *   1) HTTP/1.1 形式の 1 行（例: `GET /path?foo=bar HTTP/1.1\r\n`）の UTF-8 バイト数
 *   2) Bearer等のバッファ分の固定バイト （JWT の長さで変動するため概算）
 *      ※リクエスト行単体は 16KB 未満でもヘッダー全体で 16KB を超える時に発生する 431 を拾う
 * [含めないもの]
 *   baseURL（scheme://host）。path + query が中心。Cookie 等ブラウザ付与分。
 * [注意]
 *   実通信は HTTP/2 が多い。厳密一致ではなく request-target が支配的な目安。
 * ---------------------------------------------------------------------------
 */

/** AWS ドキュメントの Request line 上限（bytes）に合わせた閾値 */
const ALB_REQUEST_LINE_MAX_BYTES = 16_384;

/**
 * ヘッダー全体のサイズを計算するためのバッファとしての概算値
 * Accept / Content-Type / Authorization Bearer を `name: value\\r\\n` 風に足した実測値。JWT 長で変動するため概算。
 */
export const ALB_APPROX_CLIENT_HEADERS_UTF8_BYTES = 1143;

/**
 * ALB Request line 16KB の近似として、request-target（path + query）のバイト長を計測する。
 * `url` に既存クエリがあるケースでも `params` と正しく結合できるよう、path/query/hash を分解して再構築する。
 */
function measureAlbRequestLineUtf8Length(input: {
  method?: string;
  url?: string;
  params: NonNullable<AxiosRequestConfig['params']>;
}): { path: string; requestLineByteLength: number } {
  const originalPath = input.url ?? '';
  const [withoutHash] = originalPath.split('#');
  const [pathname = '', existingQuery = ''] = withoutHash.split('?');
  const newQueryString = stringify(input.params, QS_PARAMS_OPTIONS);
  const mergedQueryString =
    existingQuery && newQueryString
      ? `${existingQuery}&${newQueryString}`
      : existingQuery || newQueryString;
  const requestTarget =
    mergedQueryString === '' ? pathname : `${pathname}?${mergedQueryString}`;
  const method = (input.method ?? 'get').toUpperCase();
  const requestLine = `${method} ${requestTarget} HTTP/1.1\r\n`;
  return {
    path: originalPath,
    requestLineByteLength: utf8ByteLengthEncoder.encode(requestLine).length,
  };
}

export const api = axios.create({
  baseURL: apiHost,
  paramsSerializer: function (params) {
    return stringify(params, QS_PARAMS_OPTIONS);
  },
});

/*
 * params のみ対象（body のみの巨大データは URL に載らない）。
 * 挙動の詳細は `ALB_REQUEST_LINE_MAX_BYTES` 直上のブロックコメント参照。
 */
api.interceptors.request.use((config) => {
  if (!config.params) {
    return config;
  }

  const { path, requestLineByteLength } = measureAlbRequestLineUtf8Length({
    method: config.method,
    url: config.url,
    params: config.params,
  });

  const combinedBudgetApproxUtf8Bytes =
    requestLineByteLength + ALB_APPROX_CLIENT_HEADERS_UTF8_BYTES;

  if (combinedBudgetApproxUtf8Bytes <= ALB_REQUEST_LINE_MAX_BYTES) {
    return config;
  }

  const params = config.params as Record<string, unknown>;

  /** 各パラメータのサイズ情報。配列は要素数、文字列は length、それ以外は型名 */
  const paramSizes: Record<string, number | string> = {};
  for (const key of Object.keys(params)) {
    const value = params[key];
    if (Array.isArray(value)) {
      // 例: ymdList: ['2024-01-01', ...], kiList: ['001', '002', ...] → 要素数
      paramSizes[key] = value.length;
    } else if (typeof value === 'string') {
      // 例: startDate: '2024-01-01', sort: 'total', marking: 'M001' → 文字数
      paramSizes[key] = value.length;
    } else if (value !== null && typeof value === 'object') {
      // 例: ネストしたオブジェクトが渡された場合 → JSON の文字数
      paramSizes[key] = JSON.stringify(value).length;
    } else {
      // 例: excludeToday: true → 'boolean', 数値が単体で渡る場合 → 'number'
      paramSizes[key] = typeof value;
    }
  }

  const requestLineAloneExceeds =
    requestLineByteLength > ALB_REQUEST_LINE_MAX_BYTES;

  captureSentryException(
    new Error(
      'APIリクエストのヘッダーサイズがALB上限16KBを超過している可能性があります'
    ),
    'api_url_length_exceeded',
    {
      tags: {
        url_length_check: 'alb_16k_combined_budget_exceeded',
      },
      contexts: {
        url_length_check: {
          requestLineByteLength,
          approxClientHeadersUtf8Bytes: ALB_APPROX_CLIENT_HEADERS_UTF8_BYTES,
          combinedBudgetApproxUtf8Bytes,
          requestLineAloneExceeds,
          threshold: ALB_REQUEST_LINE_MAX_BYTES,
          path,
          paramSizes,
          paramKeys: Object.keys(params),
          message: requestLineAloneExceeds
            ? 'リクエスト行単体が 16KB 超過（ALB側で414エラーの疑い）。'
            : 'リクエストヘッダー全体の概算値が 16KB 超過（ALB側で431エラーの疑い）。',
        },
      },
    }
  );

  return config;
});

const apiResendHost = process.env.REACT_APP_API_RESEND_HOST;

export const apiResend = axios.create({
  baseURL: apiResendHost,
  paramsSerializer: function (params) {
    return stringify(params, QS_PARAMS_OPTIONS);
  },
});

export const mockApi = axios.create({
  baseURL: apiHost,
});

export type Api = typeof api;
export type ApiResend = typeof apiResend;

export function buildConfig(
  params?: AxiosRequestConfig['params']
): AxiosRequestConfig {
  return {
    params,
  };
}

export function buildDetailConfig(
  token: string,
  config?: AxiosRequestConfig
): AxiosRequestConfig {
  return {
    ...config,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...config?.headers,
    },
  };
}
